import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  MessageSquare, Plus, Edit, Trash2, Search, 
  X, Check, Star, CheckCircle2, Eye, EyeOff
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';

import API from '../../config/api';

const AdminTestimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    location: '',
    rating: 5,
    quote: '',
    verified: true,
    order: 0,
    is_active: true
  });

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/testimonials?all=true`);
      setTestimonials(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error loading testimonials:', error);
      toast.error('Failed to load testimonials');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAdd = () => {
    setEditingTestimonial(null);
    setFormData({
      name: '',
      location: '',
      rating: 5,
      quote: '',
      verified: true,
      order: testimonials.length + 1,
      is_active: true
    });
    setShowModal(true);
  };

  const handleOpenEdit = (testi) => {
    setEditingTestimonial(testi);
    setFormData({
      name: testi.name || '',
      location: testi.location || '',
      rating: testi.rating !== undefined ? testi.rating : 5,
      quote: testi.quote || '',
      verified: testi.verified !== undefined ? testi.verified : true,
      order: testi.order || 0,
      is_active: testi.is_active !== undefined ? testi.is_active : true
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.quote.trim()) {
      toast.error('Customer Name and Review Quote are required');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        rating: Number(formData.rating) || 5,
        order: Number(formData.order) || 0
      };

      if (editingTestimonial) {
        await axios.put(`${API}/testimonials/${editingTestimonial.id}`, payload, { withCredentials: true });
        toast.success('Testimonial updated successfully');
      } else {
        await axios.post(`${API}/testimonials`, payload, { withCredentials: true });
        toast.success('Testimonial created successfully');
      }

      setShowModal(false);
      fetchTestimonials();
    } catch (error) {
      console.error('Error saving testimonial:', error);
      toast.error(error.response?.data?.detail || 'Failed to save testimonial');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (testi) => {
    if (!window.confirm(`Are you sure you want to delete testimonial by "${testi.name}"?`)) return;

    try {
      await axios.delete(`${API}/testimonials/${testi.id}`, { withCredentials: true });
      toast.success('Testimonial deleted successfully');
      fetchTestimonials();
    } catch (error) {
      console.error('Error deleting testimonial:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete testimonial');
    }
  };

  const filteredTestimonials = testimonials.filter(t =>
    t.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.location?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.quote?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6">
      
      {/* Top Header */}
      <div className="bg-white rounded-2xl p-5 border border-[#1A1A1A]/[0.08] shadow-xs flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-xl font-bold text-gray-900 flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-[#D9711F]" />
            Site-wide &amp; Fallback Testimonials
          </h1>
          <p className="text-xs text-gray-500">
            These customer reviews show on the Homepage and automatically serve as fallbacks for any product without custom reviews.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <div className="relative min-w-[220px]">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search reviews..."
              className="pl-9 text-xs"
            />
          </div>
          <Button
            onClick={handleOpenAdd}
            className="btn-saffron text-xs font-bold flex items-center gap-1.5 px-4 py-2 rounded-xl shadow-xs"
          >
            <Plus className="w-4 h-4" />
            Add Testimonial
          </Button>
        </div>
      </div>

      {/* Testimonials Table Card */}
      <div className="bg-white rounded-2xl border border-[#1A1A1A]/[0.08] shadow-xs overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center p-12">
            <div className="w-8 h-8 border-3 border-[#6E1B34] border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : filteredTestimonials.length === 0 ? (
          <div className="text-center p-12">
            <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-sm font-semibold text-gray-700">No testimonials found</p>
            <p className="text-xs text-gray-400 mt-1">Add your first customer testimonial</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="bg-gray-50 border-b border-gray-100 text-gray-500 font-bold uppercase tracking-wider">
                <tr>
                  <th className="py-3.5 px-4">Order</th>
                  <th className="py-3.5 px-4">Customer</th>
                  <th className="py-3.5 px-4">Rating</th>
                  <th className="py-3.5 px-4">Review Quote</th>
                  <th className="py-3.5 px-4 text-center">Verified</th>
                  <th className="py-3.5 px-4 text-center">Status</th>
                  <th className="py-3.5 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 font-medium text-gray-800">
                {filteredTestimonials.map((testi, idx) => {
                  const initial = testi.avatar || (testi.name ? testi.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase() : 'PS');
                  return (
                    <tr key={testi.id || idx} className="hover:bg-gray-50/70 transition-colors">
                      <td className="py-3.5 px-4 text-gray-400 font-mono align-top">
                        #{testi.order || idx + 1}
                      </td>
                      <td className="py-3.5 px-4 align-top">
                        <div className="flex items-center gap-2.5">
                          <div className="w-8 h-8 rounded-full bg-[#6E1B34] text-white font-bold text-xs flex items-center justify-center flex-shrink-0">
                            {initial}
                          </div>
                          <div>
                            <h4 className="font-bold text-gray-900">{testi.name}</h4>
                            <p className="text-[10px] text-gray-500">{testi.location || 'India'}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3.5 px-4 align-top">
                        <div className="flex text-[#D9711F]">
                          {[...Array(testi.rating || 5)].map((_, i) => (
                            <Star key={i} className="w-3.5 h-3.5 fill-current" />
                          ))}
                        </div>
                      </td>
                      <td className="py-3.5 px-4 align-top max-w-md">
                        <p className="text-xs text-gray-700 italic line-clamp-2 leading-relaxed">
                          "{testi.quote}"
                        </p>
                      </td>
                      <td className="py-3.5 px-4 text-center align-top">
                        {testi.verified ? (
                          <span className="inline-flex items-center gap-1 text-green-700 font-bold text-[10px] bg-green-50 px-2 py-0.5 rounded">
                            <CheckCircle2 className="w-3 h-3" />
                            Verified
                          </span>
                        ) : (
                          <span className="text-gray-400 text-xs">—</span>
                        )}
                      </td>
                      <td className="py-3.5 px-4 text-center align-top">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${
                          testi.is_active 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-gray-100 text-gray-500'
                        }`}>
                          {testi.is_active ? 'Active' : 'Hidden'}
                        </span>
                      </td>
                      <td className="py-3.5 px-4 text-right align-top">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => handleOpenEdit(testi)}
                            className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-600 hover:text-[#6E1B34] transition-colors"
                            title="Edit Testimonial"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(testi)}
                            className="p-1.5 hover:bg-red-50 rounded-lg text-red-500 hover:text-red-700 transition-colors"
                            title="Delete Testimonial"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add / Edit Testimonial Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl border border-gray-100 space-y-4 animate-scale-up">
            <div className="flex items-center justify-between pb-3 border-b border-gray-100">
              <h2 className="font-heading text-base font-bold text-gray-900">
                {editingTestimonial ? 'Edit Testimonial' : 'Add New Testimonial'}
              </h2>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600 p-1"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-bold text-gray-800">Customer Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g. Priya Sharma"
                    className="mt-1 text-xs"
                    required
                  />
                </div>

                <div>
                  <Label className="text-xs font-bold text-gray-800">Location</Label>
                  <Input
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    placeholder="e.g. Bengaluru, Karnataka"
                    className="mt-1 text-xs"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-bold text-gray-800">Star Rating</Label>
                  <select
                    value={formData.rating}
                    onChange={(e) => setFormData({ ...formData, rating: Number(e.target.value) })}
                    className="w-full mt-1 border border-gray-200 rounded-xl px-3 py-2 text-xs bg-white focus:outline-none focus:border-[#D9711F]"
                  >
                    <option value={5}>⭐⭐⭐⭐⭐ (5 Stars)</option>
                    <option value={4}>⭐⭐⭐⭐ (4 Stars)</option>
                    <option value={3}>⭐⭐⭐ (3 Stars)</option>
                  </select>
                </div>

                <div>
                  <Label className="text-xs font-bold text-gray-800">Display Order</Label>
                  <Input
                    type="number"
                    value={formData.order}
                    onChange={(e) => setFormData({ ...formData, order: e.target.value })}
                    placeholder="1"
                    className="mt-1 text-xs"
                  />
                </div>
              </div>

              <div>
                <Label className="text-xs font-bold text-gray-800">Customer Review / Feedback *</Label>
                <textarea
                  value={formData.quote}
                  onChange={(e) => setFormData({ ...formData, quote: e.target.value })}
                  placeholder="e.g. Bought this for my mother before Dhanteras — she loves how authentic and energised it feels..."
                  rows={3}
                  className="w-full mt-1 border border-gray-200 rounded-xl p-2.5 text-xs text-[#2A1F16] focus:outline-none focus:border-[#D9711F] leading-relaxed"
                  required
                />
              </div>

              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                <div>
                  <p className="text-xs font-bold text-gray-900">Verified Purchase Badge</p>
                  <p className="text-[10px] text-gray-500">Display green verified buyer badge</p>
                </div>
                <input
                  type="checkbox"
                  checked={formData.verified}
                  onChange={(e) => setFormData({ ...formData, verified: e.target.checked })}
                  className="w-4 h-4 accent-[#6E1B34] rounded cursor-pointer"
                />
              </div>

              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                <div>
                  <p className="text-xs font-bold text-gray-900">Active Status</p>
                  <p className="text-[10px] text-gray-500">Show on homepage and fallback for products</p>
                </div>
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="w-4 h-4 accent-[#6E1B34] rounded cursor-pointer"
                />
              </div>

              <div className="flex items-center justify-end gap-2.5 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowModal(false)}
                  className="text-xs font-semibold"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="btn-saffron text-xs font-bold px-5"
                >
                  {submitting ? 'Saving...' : editingTestimonial ? 'Update Testimonial' : 'Create Testimonial'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default AdminTestimonials;
